import { useState } from "react";

import { Button, Dialog, Table } from "@fronterahealth/frontera-ui-components";
import { Checkbox } from "@fronterahealth/frontera-ui-components/ui/checkbox";
import { Textarea } from "@fronterahealth/frontera-ui-components/ui/textarea";

import { parseJson } from "@components/FieldComponent/utils";
import { ReportFieldInputBaseProps } from "@components/ReportField/types";
import { useAdminData } from "@providers/AdminDataProvider";

interface DsmCriterion {
  id: string;
  parentId?: string;
  isChecked: boolean;
  isCheckDisabled: boolean;
  description: string;
  note: string | null;
  isAllowNote: boolean;
}

const getDefaultDsmData = (isCatalightUser: boolean): DsmCriterion[] => [
  {
    id: "A",
    isChecked: false,
    isCheckDisabled: true,
    description:
      "Persistent deficits in social communication and social interaction across multiple contexts, as manifested by all of the following, currently or by history:",
    note: null,
    isAllowNote: false,
  },
  {
    id: "A1",
    parentId: "A",
    isChecked: false,
    isCheckDisabled: false,
    description: "Deficits in social-emotional reciprocity",
    note: null,
    isAllowNote: true,
  },
  {
    id: "A2",
    parentId: "A",
    isChecked: false,
    isCheckDisabled: false,
    description: "Deficits in nonverbal communicative behaviors used for social interaction",
    note: null,
    isAllowNote: true,
  },
  {
    id: "A3",
    parentId: "A",
    isChecked: false,
    isCheckDisabled: false,
    description: "Deficits in developing, maintaining, and understanding relationships",
    note: null,
    isAllowNote: true,
  },
  {
    id: "B",
    isChecked: false,
    isCheckDisabled: true,
    description:
      "Restricted, repetitive patterns of behavior, interests, or activities, as manifested by at least two of the following, currently or by history:",
    note: null,
    isAllowNote: false,
  },
  {
    id: "B1",
    parentId: "B",
    isChecked: false,
    isCheckDisabled: false,
    description: "Stereotyped or repetitive motor movements, use of objects, or speech",
    note: null,
    isAllowNote: true,
  },
  {
    id: "B2",
    parentId: "B",
    isChecked: false,
    isCheckDisabled: false,
    description:
      "Insistence on sameness, inflexible adherence to routines, or ritualized patterns of verbal or nonverbal behavior",
    note: null,
    isAllowNote: true,
  },
  {
    id: "B3",
    parentId: "B",
    isChecked: false,
    isCheckDisabled: false,
    description: "Highly restricted, fixated interests that are abnormal in intensity or focus",
    note: null,
    isAllowNote: true,
  },
  {
    id: "B4",
    parentId: "B",
    isChecked: false,
    isCheckDisabled: false,
    description: "Hyper- or hyporeactivity to sensory input or unusual interest in sensory aspects of the environment",
    note: null,
    isAllowNote: true,
  },
  {
    id: "C",
    isChecked: isCatalightUser,
    isCheckDisabled: false,
    description: "Symptoms must be present in the early developmental period",
    note: null,
    isAllowNote: true,
  },
  {
    id: "D",
    isChecked: isCatalightUser,
    isCheckDisabled: false,
    description:
      "Symptoms cause clinically significant impairment in social, occupational, or other important areas of current functioning",
    note: null,
    isAllowNote: true,
  },
  {
    id: "E",
    isChecked: isCatalightUser,
    isCheckDisabled: false,
    description: "These differences are not better explained by intellectual disability or global developmental delay",
    note: null,
    isAllowNote: true,
  },
];

// Merges saved data onto the fixed default rows so a value missing a criterion (or saved
// before one existed) still renders every current criterion, with its default disabled/checked state.
const getRows = (value: string | null | undefined, isCatalightUser: boolean): DsmCriterion[] => {
  const savedRows = parseJson<DsmCriterion[]>(value) ?? [];
  return getDefaultDsmData(isCatalightUser).map((defaultCriterion) => {
    const savedCriterion = savedRows.find((row) => row.id === defaultCriterion.id);
    return savedCriterion ? { ...defaultCriterion, ...savedCriterion } : defaultCriterion;
  });
};

export const ReportFieldDsmTable: React.FC<ReportFieldInputBaseProps> = ({ value, onChange, disabled }) => {
  const { isCatalightUser } = useAdminData();
  const [isNoteDialogOpen, setIsNoteDialogOpen] = useState(false);
  const [selectedCriterionId, setSelectedCriterionId] = useState<string | null>(null);
  const [dsmNote, setDsmNote] = useState<string | null>(null);

  const rows = getRows(value, isCatalightUser);
  const selectedCriterion = rows.find((row) => row.id === selectedCriterionId) ?? null;

  const saveRows = (updatedRows: DsmCriterion[]) => onChange(JSON.stringify(updatedRows));

  const handleCheckboxChange = (record: DsmCriterion) => {
    const newRows = rows.map((criterion) =>
      criterion.id === record.id ? { ...criterion, isChecked: !criterion.isChecked } : criterion,
    );

    // Parent/child cascade: checking or unchecking a child re-derives its parent's
    // checked state from whether any child is still checked.
    if (record.parentId) {
      const hasCheckedChild = newRows.some(
        (criterion) => criterion.parentId === record.parentId && criterion.isChecked,
      );
      saveRows(
        newRows.map((criterion) =>
          criterion.id === record.parentId ? { ...criterion, isChecked: hasCheckedChild } : criterion,
        ),
      );
      return;
    }

    saveRows(newRows);
  };

  const handleSaveNote = async () => {
    if (!selectedCriterion) return;

    await saveRows(
      rows.map((criterion) => (criterion.id === selectedCriterion.id ? { ...criterion, note: dsmNote } : criterion)),
    );
    setIsNoteDialogOpen(false);
  };

  return (
    <>
      {selectedCriterion && (
        <Dialog
          open={isNoteDialogOpen}
          setOpen={setIsNoteDialogOpen}
          title={`Note for Criterion ${selectedCriterion.id}`}
          primaryButton={{
            text: "Save Note",
            buttonAction: "regular",
            onClick: handleSaveNote,
            className: "px-6",
          }}
          secondaryButton={{
            text: "Close",
            onClick: () => setIsNoteDialogOpen(false),
          }}
        >
          <Textarea
            aria-label="Note"
            autoFocus
            value={dsmNote ?? ""}
            onChange={(event) => setDsmNote(event.target.value)}
          />
        </Dialog>
      )}
      <Table
        dataTestId="dsm-table"
        defaultItemsPerPage={20}
        cellClassName="cursor-default"
        data={rows}
        columns={[
          {
            dataIndex: "isChecked",
            key: "isChecked",
            title: "",
            className: "border-r-2 align-top",
            render: (value, record) => {
              // Don't display a checkbox for a disabled, unchecked criterion (group headers
              // A and B before any of their children are checked).
              if (record.isCheckDisabled && !record.isChecked) return;
              return (
                <Checkbox
                  aria-label={record.id}
                  checked={value as boolean}
                  disabled={record.isCheckDisabled || disabled}
                  onCheckedChange={() => handleCheckboxChange(record)}
                />
              );
            },
          },
          {
            dataIndex: "description",
            key: "description",
            title: "DSM-5 Criterion",
            render: (value, record) => (
              <table className="w-full text-text-secondary">
                <tbody>
                  <tr>
                    <td className="w-[40px] align-top" data-row-checked={record.isChecked ? "true" : "false"}>
                      <b>{record.id}.</b>
                    </td>
                    <td className="align-top">{value}</td>
                    <td className="w-[120px] pl-4 align-top">
                      {!disabled && (
                        <Button
                          text={`${record.note?.length ? "Edit" : "Add"} note`}
                          appearance="text"
                          buttonAction="regular"
                          onClick={() => {
                            setSelectedCriterionId(record.id);
                            setDsmNote(record.note);
                            setIsNoteDialogOpen(true);
                          }}
                        />
                      )}
                    </td>
                  </tr>
                  {Boolean(record.note?.length) && (
                    <tr>
                      <td></td>
                      <td colSpan={2} className="pt-2">
                        <div className="bg-limestone-50 border border-limestone-200 p-2 rounded-md ph-no-capture">
                          {record.note}
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            ),
          },
        ]}
      />
    </>
  );
};
